import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Radar, Search, FileText, Wrench, Rocket, Gauge, ArrowRight, Loader2, CheckCircle2, Globe, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";

const STAGE_ICONS = [Radar, Search, FileText, Wrench, Rocket, Gauge];
const STAGE_COLORS = ["#4da3ff", "#5AC8FA", "#BF5AF2", "#FF9F0A", "#30D158", "#32D74B"];

export default function Journey() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get("/journey"); setData(res.data); }
    catch { toast.error("Failed to load journey"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const advance = async (domain, stage) => {
    setBusy(domain);
    try { await api.post(`/journey/${encodeURIComponent(domain)}/advance`, { stage }); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(null); }
  };

  const stages = data?.stages || [];
  const domains = data?.domains || [];
  const emergentUrl = data?.emergentUrl || "https://app.emergent.sh";

  const nextAction = (d) => {
    const s = d.reachedStage;
    if (s <= 1) return { label: "Generate remediation plan", onClick: () => navigate(`/domain/${d.domain}`), primary: true };
    if (s === 2) return { label: "Start fixing / rebuild", onClick: () => advance(d.domain, 3), primary: true };
    if (s === 3) return { label: "Mark as hosted on Emergent", onClick: () => advance(d.domain, 4), primary: true };
    if (s === 4) return { label: "Activate Command Center", onClick: () => advance(d.domain, 5), primary: true };
    return { label: "Monitoring active", onClick: () => navigate("/command"), primary: false };
  };

  return (
    <Layout>
      <div className="mb-8">
        <p className="text-[color:var(--ws-accent,#4da3ff)] text-xs font-semibold uppercase tracking-[0.2em] mb-1" data-testid="journey-eyebrow">Modernization Journey</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white" data-testid="journey-title">Scan → Fix → Modernize</h1>
        <p className="text-zinc-400 text-base mt-1 max-w-2xl">One guided path from a raw scan to a fully hosted, AI-monitored business. Every website you track moves through these six stages.</p>
      </div>

      {/* Pipeline stepper */}
      <div className="bg-[#121212] border border-white/10 rounded-xl p-6 mb-8 overflow-x-auto" data-testid="journey-pipeline">
        <div className="flex items-center gap-2 min-w-[720px]">
          {stages.map((st, i) => {
            const Icon = STAGE_ICONS[i];
            const color = STAGE_COLORS[i];
            return (
              <div key={st.key} className="flex items-center gap-2 flex-1">
                <div className="flex flex-col items-center text-center flex-1">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-2" style={{ background: `${color}1a`, border: `1px solid ${color}40` }}>
                    <Icon size={19} style={{ color }} />
                  </div>
                  <p className="text-white text-xs font-semibold">{st.title}</p>
                  <p className="text-zinc-500 text-[11px] leading-tight mt-0.5 max-w-[130px]">{st.desc}</p>
                </div>
                {i < stages.length - 1 && <ArrowRight size={16} className="text-zinc-600 shrink-0 mb-8" />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Per-domain progress */}
      <h2 className="font-heading font-semibold text-white text-sm uppercase tracking-wide mb-3">Your websites</h2>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#007AFF]" /></div>
      ) : domains.length === 0 ? (
        <div className="text-center py-16 border border-white/10 rounded-xl" data-testid="journey-empty">
          <Globe size={40} className="text-zinc-600 mx-auto mb-3" />
          <p className="text-white font-medium">Start your first journey.</p>
          <p className="text-zinc-500 text-sm mt-1">Scan a website to begin the Scan → Fix → Modernize pipeline.</p>
          <button data-testid="journey-scan-cta" onClick={() => navigate("/dashboard")} className="btn-brand text-white rounded-lg px-4 py-2 text-sm mt-4 inline-flex items-center gap-2">
            <Radar size={15} /> Scan a website
          </button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="journey-domains">
          {domains.map((d) => {
            const action = nextAction(d);
            const complete = d.reachedStage >= 5;
            return (
              <div key={d.domain} data-testid={`journey-domain-${d.domain}`} className="bg-[#121212] border border-white/10 rounded-xl p-5">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                  <div className="flex items-center gap-2 min-w-0">
                    <button onClick={() => navigate(`/domain/${d.domain}`)} className="text-white font-medium hover:underline truncate">{d.domain}</button>
                    {complete
                      ? <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full text-[#32D74B] bg-[#32D74B]/12"><CheckCircle2 size={11} /> Fully modernized</span>
                      : <span className="text-zinc-500 text-xs">{d.issues} issue{d.issues === 1 ? "" : "s"} found</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {(d.reachedStage === 2 || d.reachedStage === 3) && (
                      <a data-testid={`emergent-link-${d.domain}`} href={emergentUrl} target="_blank" rel="noreferrer" className="text-sm text-[color:var(--ws-accent,#4da3ff)] border border-[color:var(--ws-accent,#4da3ff)]/30 rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5 hover:bg-[color:var(--ws-accent,#4da3ff)]/10 transition-colors">
                        Rebuild on Emergent <ExternalLink size={13} />
                      </a>
                    )}
                    <button
                      data-testid={`journey-action-${d.domain}`}
                      onClick={action.onClick}
                      disabled={busy === d.domain}
                      className={`text-sm rounded-lg px-4 py-1.5 inline-flex items-center gap-2 transition-colors disabled:opacity-60 ${action.primary ? "btn-brand text-white" : "border border-white/15 text-zinc-300 hover:bg-white/10"}`}
                    >
                      {busy === d.domain ? <Loader2 size={14} className="animate-spin" /> : null}
                      {action.label}
                      {action.primary && <ArrowRight size={14} />}
                    </button>
                  </div>
                </div>
                {/* Progress segments */}
                <div className="flex items-center gap-1.5">
                  {stages.map((st, i) => {
                    const done = i <= d.reachedStage;
                    return (
                      <div key={st.key} className="flex-1" title={st.title}>
                        <div className="h-1.5 rounded-full transition-colors" style={{ background: done ? STAGE_COLORS[i] : "#ffffff12" }} />
                        <p className={`text-[10px] mt-1.5 truncate ${done ? "text-zinc-300" : "text-zinc-600"}`}>{st.title}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
